import { useProvidedTheme } from '@/hooks/use-provided-theme';
import SimpleDialog from '@/libs/material/simple-dialog';
import { SimpleDialogBackground } from '@/libs/material/simple-dialog/types';
import { routes } from '@/utils/routes';
import {
    EnumButtonName,
    EnumDialogName,
} from '@calm/evexia-analytics/generated/typescript-axios';
import { DS_BREAKPOINT, DS_HEIGHT } from '@calm/evexia-design-tokens';
import LoginMethod from '@rhg/components/login-method';
import { QrCode } from '@rhg/components/qr-code';
import commonMessages from '@rhg/lang/messages/common';
import { Box, Theme, ThemeProvider, useTheme } from '@rhg/libs/material';
import { useId } from 'react';
import { useIntl } from 'react-intl';
import { Heading } from '../heading';
import { qrCodeDialogMessages } from './messages';

const styleDialogContent = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: DS_BREAKPOINT.LARGE,
    maxWidth: '100%',
};
const styleQrCode = {
    width: DS_HEIGHT.QR_CODE,
    height: DS_HEIGHT.QR_CODE,
    '& img': {
        width: '100%',
        height: '100%',
        scale: 'calc(140/128)',
        borderRadius: 3,
    },
    mb: (theme: Theme) => (theme.palette.mode === 'light' ? 0 : 4),
};

export interface QrCodeDialogProps {
    isOpen: boolean;
    onClose: () => void;
    trackDialog?: EnumDialogName;
    trackCloseButton?: EnumButtonName;
}

export const QrCodeDialog = ({
    isOpen,
    onClose,
    trackDialog = 'LookingForCalmApp',
    trackCloseButton = 'UseMobileAppBannerClose',
}: QrCodeDialogProps) => {
    const id = useId();
    const { formatMessage } = useIntl();
    const theme = useTheme();
    const lightTheme = useProvidedTheme('light');
    const darkTheme = useProvidedTheme('dark');
    const isDarkMode = theme.palette.mode === 'dark';

    return (
        <ThemeProvider theme={isDarkMode ? lightTheme : darkTheme}>
            <SimpleDialog
                trackDialog={trackDialog}
                aria-labelledby={id}
                maxWidth="lg"
                background={SimpleDialogBackground.Content}
                isOpen={isOpen}
                trackCloseButton={trackCloseButton}
                onClose={onClose}
                data-testid="qr-code-dialog"
                content={
                    <Box sx={styleDialogContent}>
                        <QrCode
                            url={routes.external.appDownloadDeeplink}
                            sx={styleQrCode}
                        />
                        <Heading
                            titleVariant="h6"
                            titleColor="text.body"
                            title={formatMessage(
                                qrCodeDialogMessages.downloadAppCopy
                            )}
                            titleId={id}
                            titleAlign="center"
                            subtitle={formatMessage(
                                commonMessages['common.scan_qr_code']
                            )}
                            subtitleVariant="body1"
                            subtitleColor="text.body"
                            subtitleAlign="center"
                            sx={{ my: 4 }}
                        />
                        <LoginMethod showCaption sx={{ mt: 2 }} />
                    </Box>
                }
            />
        </ThemeProvider>
    );
};
